/**
 * 需要想定 daily の 48コマ表カード幅 (= 2 カラム時の grid 1 列目) をユーザー操作で
 * 可変にし、localStorage へ永続化する hook。
 *
 * design/ui-feasibility/components/panel-resizer.md および proto の
 * `js/panel-resizer.js` + `demand/forecast-today.html` の attach 設定に対応する:
 * - clamp 範囲 300〜1200px / キーボード 1 押下 16px / localStorage キー `_dftTableW`
 * - 幅は「ドラッグ中は保存せず、離した時とキー操作時に保存」(proto の onUp / onKey と同じ)
 *
 * 既定幅 (= ユーザーがまだ幅を決めていない時の幅) は呼び出し元が渡す。proto と同じく
 * 「表がちょうど収まる幅」で、列構成から `naturalKomaTableCardWidth` が出す
 * (#6668。以前は 480px 固定で、列の合計より広いぶん列が間延びしグラフが狭かった)。
 */
import { useCallback, useLayoutEffect, useState } from 'react'

/** proto `demand/forecast-today.html` の attach 設定と同値 */
export const TABLE_MIN_WIDTH = 300
export const TABLE_MAX_WIDTH = 1200
export const TABLE_RESIZE_STEP = 16
/**
 * 呼び出し元が既定幅を出せない時のフォールバック (列構成が未確定な初回描画等)。
 * 通常は `naturalKomaTableCardWidth` の戻り値が使われるのでこの値は表に出ない。
 */
export const TABLE_FALLBACK_WIDTH = 480

/** リサイザーのハンドルを置く grid トラック幅 (design forecast-today.html と同値) */
export const RESIZER_TRACK_WIDTH = 8
/** grid の列間 (design の `gap: 0 var(--space-3)` と同値) */
export const COLUMN_GAP = 12
/** グラフ列に必ず残す幅。これを割るとグラフが読めなくなる */
export const CHART_MIN_WIDTH = 360
/** 表の列がコンテナ幅に対して譲るべき量 (ハンドル + gap 2 個 + グラフ最小幅) */
export const RESERVED_FOR_CHART =
  CHART_MIN_WIDTH + RESIZER_TRACK_WIDTH + COLUMN_GAP * 2

/** proto と同じキー。localStorage は origin 単位なのでプロトと衝突しない */
const STORAGE_KEY = '_dftTableW'

export function clampTableWidth(width: number): number {
  return Math.min(TABLE_MAX_WIDTH, Math.max(TABLE_MIN_WIDTH, width))
}

/**
 * 保存済みの幅を読む。**「未保存」と「保存済み」を null で区別する** のが要点で、
 * 未保存の間だけ既定幅 (列構成から決まる自然幅) に追随させるため。
 * localStorage が使えない環境 (Safari private 等) や壊れた値も未保存として扱う。
 */
function readStoredWidth(): number | null {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === null) return null
    const parsed = Number.parseInt(saved, 10)
    if (Number.isNaN(parsed)) return null
    return clampTableWidth(parsed)
  } catch {
    return null
  }
}

type ResizableTableWidth = {
  width: number
  /** persist=false はドラッグ中の逐次更新 (保存しない) */
  changeWidth: (width: number, persist: boolean) => void
  /** 保存幅を破棄して既定幅へ戻す (ハンドルのダブルクリック) */
  resetWidth: () => void
}

export function useResizableTableWidth(
  defaultWidth: number = TABLE_FALLBACK_WIDTH,
): ResizableTableWidth {
  // null = ユーザーがまだ幅を決めていない。その間は defaultWidth に追随する
  // (参照列を採用して列が増えた瞬間に横スクロールが出ないようにするため)。
  const [storedWidth, setStoredWidth] = useState(readStoredWidth)

  const changeWidth = useCallback((next: number, persist: boolean) => {
    const clamped = clampTableWidth(next)
    setStoredWidth(clamped)
    if (!persist) return
    try {
      window.localStorage.setItem(STORAGE_KEY, String(clamped))
    } catch {
      // 永続化できなくても幅の変更自体は成立させる
    }
  }, [])

  const resetWidth = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // 削除できなくても既定幅へは戻す
    }
    setStoredWidth(null)
  }, [])

  return {
    width: storedWidth ?? clampTableWidth(defaultWidth),
    changeWidth,
    resetWidth,
  }
}

/**
 * コンテナ幅から表の上限幅を出す。グラフ側に必ず CHART_MIN_WIDTH を残しつつ、
 * 表自身の下限 (300px) は割らない (割るほど狭い画面は 1 カラム表示なので、
 * そもそもリサイザーが出ない)。
 */
export function availableTableWidthFor(containerWidth: number): number {
  return clampTableWidth(containerWidth - RESERVED_FOR_CHART)
}

/**
 * 実際のワークスペース幅から「表を広げてよい上限」を測る hook。
 *
 * 保存幅 (最大 1200px) をそのまま state に持つと、それが収まらない画面では
 * CSS 側だけがクランプされ、**state / aria と実描画幅が乖離する**。その状態で
 * 左へドラッグしても、state が実描画幅を下回るまで見た目が一切動かない
 * (= 数百 px 空振りする) ため、上限そのものをコンテナ幅から決める。
 *
 * **引数は ref オブジェクトではなく要素そのものを取る。** ワークスペースは
 * ローディング完了後に初めてマウントされるので、ref オブジェクトを依存にすると
 * 「初回は null で早期 return → 要素が入っても再実行されない」で計測が一生
 * 走らない (callback ref + state で要素を渡すこと)。
 *
 * ResizeObserver が使えない環境では計測を諦め、静的な上限にフォールバックする
 * (CSS の min() が最終的な安全弁として残っているため、崩れはしない)。
 */
export function useAvailableTableWidth(container: HTMLElement | null): number {
  const [availableMax, setAvailableMax] = useState(TABLE_MAX_WIDTH)

  useLayoutEffect(() => {
    if (container === null) return

    const measure = (): void => {
      const containerWidth = container.clientWidth
      // 初回描画前や非表示時は 0 になる。0 を採ると上限が下限まで落ちるので測り直す
      if (containerWidth === 0) return
      const next = availableTableWidthFor(containerWidth)
      setAvailableMax((prev) => (prev === next ? prev : next))
    }

    // 初回計測は ResizeObserver の有無に依らず必ず行う。ここを observer の中だけに
    // すると、非対応環境で上限が 1200 に張り付き、CSS だけが縮んで state / aria と
    // 実描画が乖離する (= ドラッグが空振りする) 状態に戻る。
    measure()

    // 非対応環境の代替: ウィンドウリサイズで測り直す
    // (useForecastChartHeight と同じ手当て)。
    window.addEventListener('resize', measure)

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure)
      resizeObserver.observe(container)
    }

    return () => {
      window.removeEventListener('resize', measure)
      if (resizeObserver !== undefined) {
        resizeObserver.disconnect()
      }
    }
  }, [container])

  return availableMax
}
